import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { dashboardAPI, stockAPI, hrAPI } from '../services/api';
import HelpAssistant from '../components/HelpAssistant';
import { formatCurrency } from '../utils/formatters';
import toast from 'react-hot-toast';
import { 
  LayoutDashboard, Calculator, ShoppingCart, TrendingUp, Package, 
  Truck, Warehouse, Users, FileText, Settings, LogOut,
  DollarSign, AlertTriangle, CheckCircle,
  Sun, Moon, Mail, X
} from 'lucide-react';

const APP_VERSION = 'v1.0.0';
const LICENSE_PLANS = [
  { id: '1-year', title: '1 Yıl', price: 20000, description: 'Yıllık lisans yenilemesi için ideal paket.' },
  { id: '3-year', title: '3 Yıl', price: 35000, description: 'Daha avantajlı uzun dönem kullanım paketi.' },
  { id: '5-year', title: '5 Yıl', price: 50000, description: 'En ekonomik ve uzun süreli lisans secenegi.' },
];
const PAYMENT_METHODS = [
  { id: 'credit-card', title: 'Kredi Kartı', description: 'Tum bankalarin kredi kartlari ile odeme yapin.' },
  { id: 'debit-card', title: 'Banka Kartı', description: 'Banka kartiniz ile aninda guvenli odeme yapin.' },
  { id: 'bank-transfer', title: 'Havale / EFT', description: 'Banka hesabina havale veya EFT ile odeme gonderin.' },
];

const MainDashboard = () => {
  const displayedVersion = window.ROYSIS_DESKTOP?.version ? `v${window.ROYSIS_DESKTOP.version}` : APP_VERSION;
  const { user, hasPermission, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [stockStats, setStockStats] = useState({});
  const [hrStats, setHrStats] = useState({});
  const [recentActivities, setRecentActivities] = useState([]);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const isAdmin = user?.role?.toLowerCase() === 'admin';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleLicensePlanClick = (plan) => {
    setSelectedPlan(plan);
    toast.success(`${plan.title} lisans paketi secildi: ${formatCurrency(plan.price)}`);
  };

  const handlePaymentMethodClick = (method) => {
    if (!selectedPlan) {
      return;
    }

    toast.success(
      `${selectedPlan.title} icin ${method.title} secildi. Satin alma ekibi sizinle iletisime gececek.`
    );
  };

  useEffect(() => {
    fetchAllStats();
  }, []);

  const fetchAllStats = async () => {
    try {
      // Dashboard stats - her zaman çalışmalı
      const dashboardRes = await dashboardAPI.getStats().catch(() => ({ data: {} }));
      setStats(dashboardRes.data || {});

      // Stock stats - opsiyonel
      const stockRes = await stockAPI.getSummary().catch(() => ({ data: {} }));
      setStockStats(stockRes.data || {});

      // HR stats - opsiyonel
      const hrRes = await hrAPI.getEmployeeStats().catch(() => ({ data: {} }));
      setHrStats(hrRes.data || {});

      // Recent activities - opsiyonel
      const activitiesRes = await dashboardAPI.getRecentActivities().catch(() => ({ data: [] }));
      setRecentActivities(activitiesRes.data || []);
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
    }
  };

  const modules = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Anasayfa', color: 'bg-blue-500', description: 'Genel bakış ve istatistikler', permission: 'dashboard' },
    { path: '/accounting', icon: Calculator, label: 'Muhasebe', color: 'bg-green-500', description: 'Faturalar ve giderler', permission: 'accounting' },
    { path: '/sales', icon: ShoppingCart, label: 'Satış', color: 'bg-purple-500', description: 'Müşteriler ve ürünler', permission: 'sales' },
    { path: '/orders', icon: FileText, label: 'Siparişler', color: 'bg-indigo-500', description: 'Sipariş yönetimi', permission: 'orders' },
    { path: '/marketing', icon: TrendingUp, label: 'Pazarlama', color: 'bg-pink-500', description: 'Kampanyalar ve CRM', permission: 'marketing' },
    { path: '/production', icon: Package, label: 'Üretim', color: 'bg-orange-500', description: 'Üretim emirleri', permission: 'production' },
    { path: '/shipping', icon: Truck, label: 'Nakliye', color: 'bg-cyan-500', description: 'Kargo ve lojistik', permission: 'shipping' },
    { path: '/stock', icon: Warehouse, label: 'Stok', color: 'bg-yellow-500', description: 'Hammaddeler ve depolar', permission: 'stock' },
    { path: '/hr', icon: Users, label: 'İnsan Kaynakları', color: 'bg-red-500', description: 'Personel ve devam', permission: 'hr' },
    { path: '/messages', icon: Mail, label: 'Mesajlar', color: 'bg-teal-500', description: 'İç yazışmalar ve mailler', permission: 'dashboard' },
    { path: '/users', icon: Settings, label: 'Kullanıcı Yönetimi', color: 'bg-gray-500', description: 'Kullanıcılar ve yetkiler', permission: 'users' },
  ];

  const accessibleModules = modules.filter(module => hasPermission(module.permission));

  return (
    <div className={`p-6 min-h-screen ${isDark ? 'bg-gray-900' : ''}`} style={!isDark ? { background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)' } : {}}>
      {/* Demo Warning */}
      {user?.isDemo && (
        <div className="mb-6 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl shadow-lg p-4 text-white flex items-center justify-between">
          <div className="flex items-center">
            <div className="bg-white/20 p-2 rounded-lg mr-4">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Demo Sürümündesiniz</h3>
              <p className="text-sm opacity-90">Her modül için en fazla 5 işlem hakkınız bulunmaktadır. Sınırsız erişim için lisans satın alın.</p>
              <p className="mt-2 text-xs opacity-95 rounded-lg border border-white/30 bg-white/10 px-3 py-2">
                Demo hesaptaki veriler herkese açıktır, lütfen kişisel veya hassas veri girişi yapmayınız.
              </p>
            </div>
          </div>
          <button 
            onClick={() => {
              setShowLicenseModal(true);
              setSelectedPlan(null);
            }}
            data-help-target="portal-license-button"
            className="bg-white text-orange-600 px-6 py-2 rounded-lg font-bold hover:bg-orange-50 transition-colors shadow-md"
          >
            Lisans Satın Al
          </button>
        </div>
      )}

      {/* Header */}
      <div className={`${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'} rounded-xl shadow-lg p-6 mb-6`}>
        <div className="flex justify-between items-center">
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>ROYSİS ERP Programı</h1>
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Versiyon {displayedVersion}</p>
          </div>
          <div className="flex items-center gap-6">
            {isAdmin && (
              <div className="flex gap-4">
                <div className={`${isDark ? 'bg-gray-700' : 'bg-slate-50'} px-4 py-2 rounded-lg border ${isDark ? 'border-gray-600' : 'border-slate-200'}`}>
                  <p className="text-[10px] uppercase font-bold text-slate-500">Bugünkü Satış</p>
                  <p className={`text-sm font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>{formatCurrency(stats.dailySales)}</p>
                </div>
                <div className={`${isDark ? 'bg-gray-700' : 'bg-slate-50'} px-4 py-2 rounded-lg border ${isDark ? 'border-gray-600' : 'border-slate-200'}`}>
                  <p className="text-[10px] uppercase font-bold text-slate-500">Açık Alacak</p>
                  <p className={`text-sm font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>{formatCurrency(stats.totalReceivables)}</p>
                </div>
              </div>
            )}
            <div className="text-right">
              <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Hoş geldiniz, {user?.username || 'Kullanıcı'}</p>
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{user?.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}</p>
            </div>
            <button
              onClick={toggleTheme}
              className={`p-3 ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-purple-100 hover:bg-purple-200'} rounded-full transition`}
              title={isDark ? 'Aydınlık Mod' : 'Karanlık Mod'}
            >
              {isDark ? <Sun className="text-yellow-400" size={24} /> : <Moon className="text-purple-600" size={24} />}
            </button>
            <div className={`p-3 ${isDark ? 'bg-gray-700' : 'bg-purple-100'} rounded-full`}>
              <Users className={isDark ? 'text-purple-400' : 'text-purple-600'} size={24} />
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
              title="Çıkış Yap"
            >
              <LogOut size={20} />
              <span className="hidden md:inline">Çıkış</span>
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        {isAdmin && (
          <div className={`${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'} rounded-xl shadow-lg p-4`}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg"><DollarSign className="text-blue-600 dark:text-blue-400" size={20} /></div>
              <div><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Günlük Satışlar</p><p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{formatCurrency(stats.dailySales)}</p></div>
            </div>
          </div>
        )}
        {isAdmin && (
          <div className={`${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'} rounded-xl shadow-lg p-4`}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg"><TrendingUp className="text-red-600 dark:text-red-400" size={20} /></div>
              <div><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Günlük Giderler</p><p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{formatCurrency(stats.dailyExpenses)}</p></div>
            </div>
          </div>
        )}
        <div className={`${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'} rounded-xl shadow-lg p-4`}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg"><AlertTriangle className="text-yellow-600 dark:text-yellow-400" size={20} /></div>
            <div><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Kritik Stok</p><p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{stockStats.low_stock || 0}</p></div>
          </div>
        </div>
        <div className={`${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'} rounded-xl shadow-lg p-4`}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg"><Users className="text-green-600 dark:text-green-400" size={20} /></div>
            <div><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Aktif Personel</p><p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{hrStats.total || 0}</p></div>
          </div>
        </div>
        {isAdmin && (
          <div className={`${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'} rounded-xl shadow-lg p-4`}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg"><DollarSign className="text-green-600 dark:text-green-400" size={20} /></div>
              <div><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Toplam Alacaklar</p><p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{formatCurrency(stats.totalReceivables)}</p></div>
            </div>
          </div>
        )}
        <div className={`${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'} rounded-xl shadow-lg p-4`}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg"><Package className="text-indigo-600 dark:text-indigo-400" size={20} /></div>
            <div><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Ürün Sayısı</p><p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{stats.totalProducts || 0}</p></div>
          </div>
        </div>
      </div>

      {/* Module Cards */}
      <div className="mb-6">
        <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-white'} mb-4`}>Modüller ({accessibleModules.length})</h2>
        {accessibleModules.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 dashboard-grid">
            {accessibleModules.map((module, index) => (
              <Link
                key={index}
                to={module.path}
                data-help-target={`portal-${module.path.replace('/', '')}`}
                className={`${isDark ? 'bg-gray-800 border border-gray-700 hover:border-gray-600' : 'bg-white'} rounded-xl shadow-lg p-4 hover:shadow-xl transition-all transform hover:scale-105`}
              >
                <div className={`w-12 h-12 ${module.color} rounded-lg flex items-center justify-center mb-3`}>
                  <module.icon className="text-white" size={24} />
                </div>
                <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{module.label}</h3>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>{module.description}</p>
              </Link>
            ))}
          </div>
        ) : (
          <div className={`${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'} rounded-xl shadow-lg p-8 text-center`}>
            <AlertTriangle className={`mx-auto ${isDark ? 'text-yellow-400' : 'text-yellow-500'} mb-4`} size={48} />
            <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'} mb-2`}>Erişim Yetkiniz Yok</h3>
            <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Modüllere erişim için yöneticinizle iletişime geçin.</p>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'} rounded-xl shadow-lg p-6`}>
          <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'} mb-4`}>Son İşlemler</h3>
          <div className="space-y-3">
            {recentActivities.length > 0 ? recentActivities.slice(0, 5).map((activity, index) => (
              <div key={index} className={`flex items-center gap-3 p-2 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} rounded-lg`}>
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-full"><CheckCircle className="text-green-600 dark:text-green-400" size={16} /></div>
                <div><p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{activity.type || 'İşlem'}</p><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{activity.description || '-'}</p></div>
              </div>
            )) : (
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-center py-4`}>Henüz işlem yok</p>
            )}
          </div>
        </div>

        <div className={`${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'} rounded-xl shadow-lg p-6`}>
          <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'} mb-4`}>Sistem Durumu</h3>
          <div className="space-y-4">
            <div className={`flex justify-between items-center p-3 ${isDark ? 'bg-green-900/20' : 'bg-green-50'} rounded-lg`}>
              <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Sunucu</span>
              <span className="px-2 py-1 bg-green-500 text-white text-xs rounded">Aktif</span>
            </div>
            <div className={`flex justify-between items-center p-3 ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} rounded-lg`}>
              <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Veritabanı</span>
              <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded">Bağlı</span>
            </div>
            <div className={`flex justify-between items-center p-3 ${isDark ? 'bg-purple-900/20' : 'bg-purple-50'} rounded-lg`}>
              <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Toplam Modül</span>
              <span className={`font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>{accessibleModules.length}</span>
            </div>
            <div className={`flex justify-between items-center p-3 ${isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'} rounded-lg`}>
              <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Kullanıcı</span>
              <span className={`font-bold ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>{user?.username || 'Kullanıcı'}</span>
            </div>
            <div className={`flex justify-between items-center p-3 ${isDark ? 'bg-indigo-900/20' : 'bg-indigo-50'} rounded-lg`}>
              <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Rol</span>
              <span className={`font-bold ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>{user?.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={`mt-6 text-center text-sm ${isDark ? 'text-gray-400' : 'text-white'}`}>
        <p>ROYSİS ETİKET ÜRETİM PROGRAMI © 2026 - Tüm hakları saklıdır</p>
        <p className="text-[10px] mt-2 uppercase tracking-[0.2em] font-bold opacity-80">
          produced by penguen
        </p>
      </div>

      {showLicenseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className={`w-full max-w-4xl rounded-2xl shadow-2xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
            <div className={`flex items-center justify-between border-b px-6 py-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div>
                <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Lisans Satin Alma Secenekleri</h3>
                <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Ihtiyaciniza uygun sureyi secin. Paket secildikten sonra odeme yontemleri acilir.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowLicenseModal(false);
                  setSelectedPlan(null);
                }}
                className={`rounded-full p-2 transition ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-500'}`}
                aria-label="Kapat"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid gap-4 p-6 md:grid-cols-3">
              {LICENSE_PLANS.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => handleLicensePlanClick(plan)}
                  className={`rounded-2xl border p-5 text-left transition hover:-translate-y-1 hover:shadow-xl ${
                    isDark
                      ? `border-gray-700 bg-gray-900 hover:border-orange-400 ${selectedPlan?.id === plan.id ? 'ring-2 ring-orange-400 border-orange-400' : ''}`
                      : `border-gray-200 bg-orange-50/40 hover:border-orange-400 ${selectedPlan?.id === plan.id ? 'ring-2 ring-orange-400 border-orange-400' : ''}`
                  }`}
                >
                  <p className="text-sm font-semibold uppercase tracking-wide text-orange-500">{plan.title}</p>
                  <p className={`mt-3 text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(plan.price)}</p>
                  <p className={`mt-3 text-sm leading-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{plan.description}</p>
                  <div className={`mt-5 inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold ${
                    isDark ? 'bg-orange-500 text-white' : 'bg-orange-500 text-white'
                  }`}>
                    Paketi Sec
                  </div>
                </button>
              ))}
            </div>

            {selectedPlan && (
              <div className={`border-t px-6 py-5 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="mb-4">
                  <h4 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    Odeme Secenekleri
                  </h4>
                  <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>
                    Secilen paket: {selectedPlan.title} - {formatCurrency(selectedPlan.price)}
                  </p>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  {PAYMENT_METHODS.map((method) => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => handlePaymentMethodClick(method)}
                      className={`rounded-xl border p-4 text-left transition hover:-translate-y-1 hover:shadow-lg ${
                        isDark
                          ? 'border-gray-700 bg-gray-900 hover:border-blue-400'
                          : 'border-gray-200 bg-blue-50/40 hover:border-blue-400'
                      }`}
                    >
                      <p className="font-semibold text-blue-500">{method.title}</p>
                      <p className={`mt-2 text-sm leading-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {method.description}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={`flex flex-col gap-3 border-t px-6 py-4 text-sm md:flex-row md:items-center md:justify-between ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
              <p>Secim yaptiginizda satin alma ekibi sizinle iletisime gecmek uzere kayit olusturabilir.</p>
              <button
                type="button"
                onClick={() => {
                  setShowLicenseModal(false);
                  setSelectedPlan(null);
                }}
                className={`rounded-lg px-4 py-2 font-semibold transition ${isDark ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
      <HelpAssistant currentPath={location.pathname} isDark={isDark} />
    </div>
  );
};

export default MainDashboard;
